<script setup lang="ts">
import { UiCard, UiChip, UiText, UiIcon } from "@shopexperts/shared";
import { computed } from "vue";
import type { PartnerBadge } from "@shopexperts/shared";
import { listingStatusChip } from "../badgeListing";

interface BadgeCardProps {
  badge: PartnerBadge;
}

const props = defineProps<BadgeCardProps>();

const emit = defineEmits<{
  (e: 'edit'): void;
  (e: 'delete'): void;
  (e: 'assign'): void;
}>();

const iconUrl = computed(() => props.badge.icon?.cdn_url || props.badge.icon?.url);
const listingChip = computed(() => listingStatusChip(props.badge));
const tagLabel = computed(() => props.badge.tag === 'apps' ? 'App' : props.badge.tag === 'themes' ? 'Theme' : 'Untagged');

const formattedDate = computed(() => {
  if (!props.badge.created_at) return 'N/A';
  
  const date = new Date(props.badge.created_at);
  return date.toLocaleDateString('en-US', { 
    day: 'numeric', 
    month: 'short', 
    year: 'numeric' 
  });
});

function onEditClick() {
  emit('edit');
}

function onDeleteClick() {
  emit('delete');
}

function onAssignClick() {
  emit('assign');
}
</script>

<template>
  <UiCard padding="md" class="hover:shadow-md transition-shadow">
    <div class="flex items-center justify-between gap-4">
      <!-- Badge Info -->
      <div class="flex gap-4 items-center flex-1 min-w-0">
        <!-- Icon -->
        <div class="flex-shrink-0">
          <div v-if="iconUrl" class="w-16 h-16 rounded overflow-hidden border border-gray-200">
            <img 
              :src="iconUrl" 
              :alt="badge.name"
              class="w-full h-full object-cover"
            />
          </div>
          <div v-else class="w-16 h-16 rounded bg-gray-100 flex items-center justify-center border border-gray-200">
            <UiIcon name="image-placeholder" class="w-8 h-8 text-gray-400" />
          </div>
        </div>
        
        <!-- Name -->
        <div class="flex-1 min-w-0">
          <UiText as="p" variant="p" class="font-semibold text-gray-900 truncate">
            {{ badge.name }}
          </UiText>
          <div class="flex items-center gap-2 mt-1">
            <UiText as="p" variant="caption-normal" class="text-gray-500">
              {{ tagLabel }}
            </UiText>
            <UiChip v-if="listingChip" :text="listingChip.text" :variant="listingChip.variant" :tooltip="listingChip.tooltip" size="xs" />
          </div>
        </div>
      </div>

      <!-- Added Date -->
      <div class="flex-shrink-0 text-right">
        <UiText as="p" variant="caption-normal" class="text-gray-500">
          Added Date
        </UiText>
        <UiText as="p" variant="p" class="font-semibold text-gray-900">
          {{ formattedDate }}
        </UiText>
      </div>

      <!-- Actions -->
      <div class="flex items-center gap-4 flex-shrink-0">
        <button
          @click="onEditClick"
          class="flex items-center gap-2 text-gray-900 hover:text-gray-700 transition-colors"
        >
          <UiIcon name="pencil" class="w-5 h-5" />
          <span class="text-sm font-semibold underline">Edit Details</span>
        </button>
        
        <button
          @click="onAssignClick"
          class="flex items-center gap-2 text-gray-900 hover:text-blue-600 transition-colors"
        >
          <UiIcon name="user-circle" class="w-5 h-5" />
          <span class="text-sm font-semibold underline">Assign Expert</span>
        </button>
        
        <button
          @click="onDeleteClick"
          class="flex items-center gap-1 text-gray-900 hover:text-red-600 transition-colors"
        >
          <UiIcon name="delete" class="w-5 h-5" />
          <span class="text-sm font-semibold underline">Remove</span>
        </button>
      </div>
    </div>
  </UiCard>
</template>

